import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import CommonDatePicker from "../../../../common/components/date-picker/CommonDatePicker";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import Button from "../../../../common/components/xepp/button/Button";
import TimePicker from "../../../../common/components/xepp/time-picker/TimePicker";
import styles from "../RequestPage.module.scss";
import { ScheduleAction } from "../request.type";
import { getDefaultValues } from "../request.util";

interface ScheduleAssessmentModalProps {
	onClose: () => void;
	onSubmit: (data: { date: Date; time: { hour: string; minute: string; period: "AM" | "PM" } }) => void;
	clientPhone: string;
	actionType?: ScheduleAction;
	isLoading?: boolean;
	scheduledDate?: string;
}

interface FormData {
	date: Date;
	time: { hour: string; minute: string; period: "AM" | "PM" };
	confirmation: boolean;
}

export const ScheduleAssessmentModal = ({
	onClose,
	onSubmit,
	clientPhone,
	isLoading,
	actionType='schedule',
	scheduledDate,
}: Readonly<ScheduleAssessmentModalProps>) => {
	const {
		control,
		handleSubmit,
		watch,
		reset,
		formState: { errors, isValid }
	} = useForm<FormData>({
		mode: "onChange",
		defaultValues: getDefaultValues(scheduledDate)
	});

	useEffect(() => {
		reset(getDefaultValues(scheduledDate));
	}, [scheduledDate, reset]);

	const watchedValues = watch();
	const isFormValid = isValid && watchedValues.confirmation && !isLoading;
    
	const insText = actionType === "schedule" ? "Schedule" : "Reschedule";
	const actionText = actionType === "schedule" ? "Scheduling" : "Rescheduling";

	const handleFormSubmit = (data: FormData) => {
		onSubmit({
			date: data.date,
			time: data.time
		});
	};

	return (
		<CommonDialog onCloseDialog={onClose}>
			<div className={`${styles.modal_container} ${styles.assessment_modal}`}>
				<div className={styles.head}>
					<h4 className={styles.title}>{insText}</h4>
					<svg 
						className={styles.clickable} 
						onClick={onClose} 
						width="18" 
						height="18" 
						viewBox="0 0 18 18" 
						fill="none" 
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M15.75 15.75L9 9M9 9L2.25 2.25M9 9L15.75 2.25M9 9L2.25 15.75"
							stroke="black"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</div>

				<form onSubmit={handleSubmit(handleFormSubmit)}>
					<div className={styles.input_section}>
						<div className={styles.formSection}>
							<label>Select Schedule Date</label>
							<Controller
								name="date"
								control={control}
								rules={{ 
									required: "Please select a date",
									validate: (value) => {
										const today = new Date();
										today.setHours(0, 0, 0, 0);
										const selectedDate = new Date(value);
										selectedDate.setHours(0, 0, 0, 0);
										
										if (selectedDate < today) {
											return "Please select a future date";
										}
										return true;
									}
								}}
								render={({ field }) => (
									<>
										<CommonDatePicker
											initialDate={field.value}
											onChange={field.onChange}
										/>
										{errors.date && (
											<span className={styles.errorMessage}>{errors.date.message}</span>
										)}
									</>
								)}
							/>
						</div>

						<div className={styles.formSection}>
							<label>Select Schedule Time</label>
							<Controller
								name="time"
								control={control}
								rules={{ 
									required: "Please select a time",
									validate: (value) => {
										if (!value.hour || !value.minute || !value.period) {
											return "Please select a valid time";
										}
										return true;
									}
								}}
								render={({ field }) => (
									<>
										<TimePicker 
											value={field.value} 
											onChange={field.onChange} 
										/>
										{errors.time && (
											<span className={styles.errorMessage}>{errors.time.message}</span>
										)}
									</>
								)}
							/>
						</div>
					</div>

					<div className={styles.confirmation_text}>
						<Controller
							name="confirmation"
							control={control}
							rules={{ 
								required: "You must confirm that you've contacted the client" 
							}}
							render={({ field }) => (
								<>
									<input
										type="checkbox"
										checked={field.value}
										onChange={field.onChange}
										required
									/>
									<span>
										Confirm you've contacted client{" "}
										<strong style={{ fontWeight: 600, color: "#222222" }}>
											{clientPhone}
										</strong>{" "}
										to schedule and confirm time.
									</span>
									{errors.confirmation && (
										<div className={styles.errorMessage}>
											{errors.confirmation.message}
										</div>
									)}
								</>
							)}
						/>
					</div>

					<div className={styles.footer}>
						<Button
							onClick={onClose}
							type="button"
							text="Cancel"
							variant="outline"
							className={styles.cancel_button}
						/>
						<Button
							type="submit"
							text={isLoading ? actionText: insText}
							variant="primary"
							className={styles.schedule_button}
							disabled={!isFormValid}
						/>
					</div>
				</form>
			</div>
		</CommonDialog>
	);
};